import { BdsButton, BdsPaper, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import React, { FC, useContext, useEffect } from 'react';
import { startLoading, stopLoading } from '../../../api/CommonApi';
import { BlipTable } from '../../../components/BlipTable';
import DailyEngagedUsersContext from '../../../contexts/DailyEngagedUsersContext';
import { useCommon } from '../../../contexts/CommonContext';
import { getMonthTwoDigits } from '../../../utils/date';
import {
  DailyEngagedUserRow,
  DailyEngagedUserPaper,
  DailyEngagedUserSumUsagePaperRow,
  DailyEngagedUserUsageStatementContent,
  DailyEngagedUserConsumptionConsolidatedHeaderPartial,
  DailyEngagedUserConsumptionConsolidatedHeaderRow,
} from '../styles';
import { DateDropdown } from '../../../components/DateDropdown';
import { Link } from 'react-router-dom';
import {
  DAILY_ENGAGED_USERS_MONTHLY_BALANCE_STATEMENT_DETAIL_CLICK,
  DAILY_ENGAGED_USERS_PERIOD_SELECT_MONTHLY_OPENED,
} from '../../../shared/SegmentEvents';
import { createTrack } from '../../../api/SegmentApi';
import FeatureToggleService from '../../../services/FeatureToggleService';
import { BillingMetrics } from '../../../shared/BillingMetrics';

export interface DailyEngagedUserMonthlyUsageProps {
  registersToTake: number;
  tableBodyHeight: string;
}

const DailyEngagedUserMonthlyUsageComponent: FC<DailyEngagedUserMonthlyUsageProps> = ({
  registersToTake = -1,
  tableBodyHeight = '200px',
}) => {
  const {
    currentMonth,
    dateToSearch,
    content,
    tenant,
    loggedUser,
    sortDailyEngagedUserMonthlyUsage,
    setDailyEngagedUserMonthlyUsage,
    dailyEngagedUserMonthlyUsage,
    billingDailyEngagedUserApi,
    periodOptions,
    setDateToSearch,
  } = useContext(DailyEngagedUsersContext);
  const { setHeaderContent } = useCommon();

  const tableMonthlyUsageModel = [
    {
      label: content.chatbotDailyEngagedUserConsuption.columnChatbot,
      key: 'botName',
      hasChildContent: true,
      childContentkey: 'botId',
    },
    { label: content.chatbotDailyEngagedUserConsuption.columnDailyEngagedUsers, key: 'total' },
  ];

  useEffect(() => {
    const getData = async () => {
      startLoading();
      const storageType = await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.dailyEngagedUser) ? 1 : 0;

      const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
      const dailyEngagedUserMonthlyUsage = await billingDailyEngagedUserApi.getDailyActiveUserMonthlyUsage(
        tenant.info.id,
        monthString,
        registersToTake,
        storageType,
      );

      setDailyEngagedUserMonthlyUsage(dailyEngagedUserMonthlyUsage);

      stopLoading();
    };

    getData();

    return () => setHeaderContent({ redirect: null });
  }, [dateToSearch]);

  const trackDauMonthlyBalanceStatementDetail = async () => {
    createTrack(tenant.info, DAILY_ENGAGED_USERS_MONTHLY_BALANCE_STATEMENT_DETAIL_CLICK, {
      dauDate: dateToSearch.getFullYear() + '-' + ('0' + (dateToSearch.getMonth() + 1)).slice(-2),
      dauUser: loggedUser.email,
    });
  };

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    createTrack(tenant.info, DAILY_ENGAGED_USERS_PERIOD_SELECT_MONTHLY_OPENED, {
      dauDate: dateToRetrieveData.getFullYear() + '-' + ('0' + (dateToRetrieveData.getMonth() + 1)).slice(-2),
      dauUser: loggedUser.email,
    });
  };

  return (
    <DailyEngagedUserPaper>
      <DailyEngagedUserRow>
        <BdsTypo className='title' tag="h3" variant="fs-24" bold="bold" margin={false}>
          {content.chatbotDailyEngagedUserConsuption.title}
          <BdsTypo variant="fs-14">{content.chatbotDailyEngagedUserConsuption.about}</BdsTypo>
        </BdsTypo>
        {registersToTake > 0 ? (
          <Link to={{ pathname: '/daily-active-users/monthly', state: { DailyEngagedUsersDaily: true } }}>
            <BdsButton variant="ghost" arrow={true} size="short" onClick={trackDauMonthlyBalanceStatementDetail}>
              {content.chatbotDailyEngagedUserConsuption.buttonAllChatbots}
            </BdsButton>
          </Link>
        ) : (
          <>
            <DailyEngagedUserConsumptionConsolidatedHeaderPartial>
              <DailyEngagedUserConsumptionConsolidatedHeaderRow>
                {currentMonth && (
                  <BdsPaper className='partialUsage' elevation="static">
                    <BdsTypo variant="fs-12" bold="semi-bold">
                      {content.compiledDailyEngagedUserInformation.partialUsage}
                    </BdsTypo>
                  </BdsPaper>
                )}
              </DailyEngagedUserConsumptionConsolidatedHeaderRow>
            </DailyEngagedUserConsumptionConsolidatedHeaderPartial>
            <DateDropdown
              value={dateToSearch.toISOString()}
              bdsChangeEvent={periodOptionChangeEvent}
              options={periodOptions}
              label={content.monthPlaceholder}
            ></DateDropdown>
          </>
        )}
      </DailyEngagedUserRow>
      <DailyEngagedUserSumUsagePaperRow></DailyEngagedUserSumUsagePaperRow>
      <DailyEngagedUserUsageStatementContent>
        <BdsPaper>
          <BlipTable
            idKey="dailyEngagedUsers"
            model={tableMonthlyUsageModel}
            data={dailyEngagedUserMonthlyUsage}
            selectedItems={[]}
            onSortSet={sortDailyEngagedUserMonthlyUsage}
            canSelect={false}
            bodyHeight={tableBodyHeight}
          ></BlipTable>
        </BdsPaper>
      </DailyEngagedUserUsageStatementContent>
    </DailyEngagedUserPaper>
  );
};

export default DailyEngagedUserMonthlyUsageComponent;
